import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SiteMessage from './SiteMessage';
import { supabase } from '../utils/supabase';

/**
 * SiteMessageContainer Component
 *
 * Smart container that fetches, filters, and displays site messages based on:
 * - Current page context
 * - User targeting rules
 * - User dismissal history
 * - Message scheduling
 *
 * @param {string} page - Current page identifier ('registration', 'checkout', 'dashboard', etc.)
 * @param {string} variant - Message display variant ('full' or 'compact')
 * @param {number} maxMessages - Maximum number of messages to show at once
 * @param {string} className - Additional CSS classes
 */
const SiteMessageContainer = ({
  page = 'all',
  variant = 'full',
  maxMessages = 1,
  className = ''
}) => {
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, [page, user?.id]);

  const fetchMessages = async () => {
    try {
      setIsLoading(true);

      // Build query for active messages
      let query = supabase
        .from('site_messages')
        .select('*')
        .eq('active', true)
        .lte('start_date', new Date().toISOString())
        .order('priority', { ascending: false });

      const { data: allMessages, error } = await query;

      if (error) throw error;

      if (!allMessages || allMessages.length === 0) {
        setMessages([]);
        setIsLoading(false);
        return;
      }

      // Filter messages by date range (end_date can be null)
      const now = new Date();
      const activeMessages = allMessages.filter(msg => {
        if (!msg.end_date) return true;
        return new Date(msg.end_date) >= now;
      });

      // Load dismissed messages
      const dismissed = await loadDismissedMessages();
      setDismissedIds(new Set(dismissed));

      // Client-side filtering for user targeting and page
      const targetedMessages = activeMessages.filter(msg => {
        // Check if already dismissed
        if (dismissed.includes(msg.id)) return false;

        // Check if message should display on this page
        if (!msg.display_pages.includes('all') && !msg.display_pages.includes(page)) {
          return false;
        }

        // Check if message targets this user
        if (msg.target_audience.includes('all')) return true;

        // If not logged in, only show 'all' audience messages
        if (!user) return false;

        // Check user segment targeting
        if (msg.target_audience.includes('new_users') && isNewUser(userProfile)) return true;
        if (msg.target_audience.includes('free_users') && userProfile?.account_type === 'free') return true;
        if (msg.target_audience.includes('premium_users') && userProfile?.account_type === 'premium') return true;
        if (msg.target_audience.includes('non_subscribers') && userProfile?.account_type !== 'premium') return true;

        return false;
      });

      // Limit to maxMessages
      const visibleMessages = targetedMessages.slice(0, maxMessages);
      setMessages(visibleMessages);

      // Track message views (fire and forget)
      if (visibleMessages.length > 0) {
        trackMessageViews(visibleMessages.map(m => m.id));
      }
    } catch (error) {
      console.error('Error fetching site messages:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Check if user is considered "new" (within 7 days of signup)
   */
  const isNewUser = (profile) => {
    if (!profile?.created_at) return false;
    const daysSinceSignup = (Date.now() - new Date(profile.created_at)) / (1000 * 60 * 60 * 24);
    return daysSinceSignup <= 7;
  };

  /**
   * Load dismissed messages from localStorage and sync with database
   */
  const loadDismissedMessages = async () => {
    // Try localStorage first (instant, works for logged out users)
    const localDismissed = JSON.parse(localStorage.getItem('dismissedMessages') || '[]');

    // If logged in, sync with database for cross-device persistence
    if (user) {
      try {
        const { data, error } = await supabase
          .from('message_dismissals')
          .select('message_id')
          .eq('user_id', user.id);

        if (error) throw error;

        const dbDismissed = data?.map(d => d.message_id) || [];

        // Merge local and database dismissals
        const allDismissed = [...new Set([...localDismissed, ...dbDismissed])];
        localStorage.setItem('dismissedMessages', JSON.stringify(allDismissed));
        return allDismissed;
      } catch (error) {
        console.error('Error loading dismissals from database:', error);
      }
    }

    return localDismissed;
  };

  /**
   * Handle message dismissal
   */
  const handleDismiss = async (messageId) => {
    // Optimistic update - hide immediately
    setMessages(prev => prev.filter(m => m.id !== messageId));
    setDismissedIds(prev => new Set([...prev, messageId]));

    // Save to localStorage (works for all users)
    const dismissed = JSON.parse(localStorage.getItem('dismissedMessages') || '[]');
    dismissed.push(messageId);
    localStorage.setItem('dismissedMessages', JSON.stringify(dismissed));

    // If logged in, persist to database
    if (user) {
      try {
        const { error } = await supabase
          .from('message_dismissals')
          .insert({
            user_id: user.id,
            message_id: messageId,
            dismissed_at: new Date().toISOString()
          });

        if (error) {
          // If error (likely duplicate), just log it - localStorage still works
          console.debug('Dismissal already recorded in database');
        }
      } catch (error) {
        console.error('Error saving dismissal to database:', error);
      }
    }
  };

  /**
   * Track message views for analytics (fire and forget)
   */
  const trackMessageViews = async (messageIds) => {
    try {
      const views = messageIds.map(id => ({
        message_id: id,
        user_id: user?.id || null,
        viewed_at: new Date().toISOString(),
        page: page
      }));

      // Fire and forget - don't await
      supabase.from('message_views').insert(views);
    } catch (error) {
      // Silent fail - analytics shouldn't break UX
      console.debug('Message view tracking skipped');
    }
  };

  // Make CTA tracking available globally for SiteMessage component
  useEffect(() => {
    window.trackMessageCtaClick = async (messageId) => {
      try {
        if (user) {
          // Update existing view record to mark CTA as clicked
          const { error } = await supabase
            .from('message_views')
            .update({ cta_clicked: true })
            .eq('message_id', messageId)
            .eq('user_id', user.id)
            .order('viewed_at', { ascending: false })
            .limit(1);

          if (error) console.debug('CTA click tracking failed:', error);
        }
      } catch (error) {
        console.debug('CTA click tracking skipped');
      }
    };

    return () => {
      delete window.trackMessageCtaClick;
    };
  }, [user?.id]);

  // Don't render anything while loading or if no messages
  if (isLoading || messages.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {messages.map(message => (
        <SiteMessage
          key={message.id}
          message={message}
          variant={variant}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
};

export default SiteMessageContainer;
